import { getLocaleFromPath } from '@marketplace/helpers/url.js'

/**
 * Get a unified route name for our current app and Nuxt localizes route names.
 *
 * Example:
 * - marketplace___nl (Nuxt)
 * - marketplace (current app)
 *
 * Result:
 * Both route names result to `marketplace`.
 *
 * @param {string} routeName
 * @returns {string|undefined}
 */
export function unlocalizeRouteName(routeName) {
    if (!routeName) {
        return
    }

    const capturingRegex = /((?<name>.*?)___)/i
    const match = routeName.match(capturingRegex)

    if (!match) {
        return routeName
    }

    return match.groups.name
}

/**
 * Try to get the first referrer from the current route object. The referrer is a custom property.
 *
 * @param {object} route
 * @returns {string}
 */
export function getRefererFromRoute(route) {
    let referrer = ''

    if (route.meta?.referrer) {
        referrer = route.meta.referrer
    }

    if (Array.isArray(route?.meta)) {
        referrer = route?.meta?.find((item) => item?.referrer)?.referrer
    }

    return referrer
}

/**
 * Creates a full url (with origin) based on the route object.
 *
 * @param {object} route
 * @returns {string}
 */
export function getFullUrlFromRoute(route) {
    return new URL(route.fullPath, window.location.origin).href
}

/**
 * Get the unlocalized path from a route object.
 *
 * @param {object} route
 * @param {string} locale
 * @returns {string}
 */
export function getUnlocalizedPathFromRoute(route, locale = null) {
    const lang =
        locale || route.params.lang || getLocaleFromPath(route.path) || 'en'

    if (route.path === `/${lang}`) {
        return route.path.replace(`/${lang}`, '/')
    }

    return route.path.replace(`/${lang}/`, '/')
}

const dsRouteMapping = {
    ds: 'home',
    'ds-store-slug-listings-listingId': 'product',
    'ds-store-slug': 'storefront',
    'ds-listings': 'listings',
    'ds-marketplace': 'marketplace',
    'ds-search': 'search',
    // Add more mappings here if needed
}

/**
 * @param {string} routeName
 * @returns {string}
 */
export function normalizeDsRouteName(routeName) {
    if (!routeName) {
        return ''
    }

    // If the routeName starts with 'ds-', look up its non-DS equivalent
    if (routeName.startsWith('ds-')) {
        return dsRouteMapping[routeName] || routeName
    }
    // Else, return the routeName as-is
    return routeName
}
